// src/components/Chat/ClientEditDialog.tsx
import React, { useEffect, useState } from 'react';
import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    TextField,
} from '@mui/material';
import { createClient, IClientCard, IClientFormPayload, updateClient } from '../../api/clients';
import { apiErrorMessage } from '../../api/errors';
import { CLIENT_CHANNELS } from './clientChannels';

interface ClientEditDialogProps {
    open: boolean;
    onClose: () => void;
    /** Организация консоли; без неё бэкенд отклонит запрос. */
    mode: string;
    /** Id клиента справочника: есть — правим, нет — создаём. */
    clientId?: string | null;
    initialName?: string | null;
    initialPhone?: string | null;
    initialChannel?: string | null;
    initialNote?: string | null;
    onSaved: (client: IClientCard) => void;
}

export const ClientEditDialog: React.FC<ClientEditDialogProps> = ({
    open,
    onClose,
    mode,
    clientId,
    initialName,
    initialPhone,
    initialChannel,
    initialNote,
    onSaved,
}) => {
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [channel, setChannel] = useState('');
    const [note, setNote] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const isEdit = Boolean(clientId);

    // Заполняем форму при каждом открытии: диалог переиспользуется
    // для разных клиентов, а примитивы в зависимостях не «дёргают»
    // эффект на каждый ререндер родителя.
    useEffect(() => {
        if (!open) return;

        setName(initialName ?? '');
        setPhone(initialPhone ?? '');
        setChannel(initialChannel ?? '');
        setNote(initialNote ?? '');
        setError(null);
        setSaving(false);
    }, [open, clientId, initialName, initialPhone, initialChannel, initialNote]);

    const handleSave = async () => {
        const trimmedName = name.trim();
        if (!trimmedName) {
            setError('Укажите имя клиента.');
            return;
        }
        if (!mode) {
            setError('Выберите организацию.');
            return;
        }

        const payload: IClientFormPayload = {
            name: trimmedName,
            phone: phone.trim() ? phone.trim() : null,
            channel: channel ? channel : null,
            note: note.trim() ? note.trim() : null,
        };

        setSaving(true);
        setError(null);

        try {
            const saved =
                clientId && isEdit
                    ? await updateClient(clientId, mode, payload)
                    : await createClient(mode, payload);
            onSaved(saved);
        } catch (e) {
            setError(apiErrorMessage(e, isEdit ? 'Не удалось сохранить клиента' : 'Не удалось создать клиента'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>{isEdit ? 'Клиент' : 'Новый клиент'}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    <TextField
                        autoFocus
                        fullWidth
                        required
                        label="Имя"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        disabled={saving}
                    />
                    <TextField
                        fullWidth
                        label="Телефон"
                        placeholder="+7 900 000-00-00"
                        value={phone}
                        onChange={(event) => setPhone(event.target.value)}
                        disabled={saving}
                    />
                    <TextField
                        select
                        fullWidth
                        label="Канал связи"
                        value={channel}
                        onChange={(event) => setChannel(event.target.value)}
                        disabled={saving}
                    >
                        <MenuItem value="">Не указан</MenuItem>
                        {CLIENT_CHANNELS.map((item) => (
                            <MenuItem key={item.value} value={item.value}>
                                {item.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label="Заметка"
                        value={note}
                        onChange={(event) => setNote(event.target.value)}
                        disabled={saving}
                    />
                    {error ? <Alert severity="error">{error}</Alert> : null}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    Отмена
                </Button>
                <Button
                    variant="contained"
                    onClick={() => void handleSave()}
                    disabled={saving || !mode}
                    startIcon={saving ? <CircularProgress color="inherit" size={16} /> : undefined}
                >
                    {isEdit ? 'Сохранить' : 'Создать'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ClientEditDialog;
